import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Background, 
  Controls, 
  ReactFlow, 
  ReactFlowProvider, 
  useReactFlow,
  Connection,
  Edge,
  Node,
  addEdge,
  OnConnect,
  OnEdgesChange,
  OnNodesChange,
  useNodesState,
  useEdgesState
} from '@xyflow/react';
import { 
  Dialog, 
  Drawer, 
  TextField, 
  Stack, 
  Typography, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Popover, 
  Tooltip,
  Button,
  CircularProgress,
  Box
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { Wand2 } from 'lucide-react';
import AgentNodeBox from '../components/Nodes/AgentNodeBox';
import StickyNote from '../components/Nodes/StickyNote';
import FlowControls from '../components/FlowEditor/FlowControls';
import FlowHeader from '../components/FlowEditor/FlowHeader';
import WorkflowInputsDrawer from '../components/FlowEditor/WorkflowInputsDrawer';
import WorkflowJsonDrawer from '../components/FlowEditor/WorkflowJsonDrawer';
import GenerateWorkflowModal from '../components/FlowEditor/GenerateWorkflowModal';
import AgentNode from '../models/Nodes/AgentNode';
import SemanticMemory from '../models/Memories/SemanticMemory';
import ProceduralMemory from '../models/Memories/ProceduralMemory';
import FileUploadModal from '../components/FileUpload/UploadFileBox';
import UnsavedChangesModal from '../components/Modals/UnsavedChangesModal';
import ExecuteWorkflowModal from '../components/Modals/ExecuteWorkflowModal';
import WorkflowDiffModal from '../components/Modals/WorkflowDiffModal';
import WorkflowIdModal from '../components/Modals/WorkflowIdModal';
import AddNodeModal from '../components/Modals/AddNodeModal';
import NodeEditWizardModal from '../components/Modals/NodeEditWizardModal';
import InputNodeModal from '../components/Modals/InputNodeModal';
import { useEnvironment } from '../context/EnvironmentContext';
import WorkflowService from '../services/WorkflowService';
import { CustomNode, CustomEdge, WorkflowJson, NodeData } from '../types/flow';
import JSONView from '@uiw/react-json-view';
import { ContentCopy as CopyIcon, Code as CodeIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useApiToken } from '../hooks/useApiToken';

const nodeTypes = {
  base: AgentNodeBox,
  stickyNote: StickyNote,
};

const NODE_WIDTH = 300;
const NODE_HEIGHT = 150;
const GRID_PADDING = 100;

const STICKY_NOTE_COLORS = [
  { name: 'Yellow', value: 'yellow', class: 'bg-yellow-500' },
  { name: 'Red', value: 'red', class: 'bg-red-500' },
  { name: 'Blue', value: 'blue', class: 'bg-blue-500' },
  { name: 'Green', value: 'green', class: 'bg-green-500' },
  { name: 'Purple', value: 'purple', class: 'bg-purple-500' },
];

const generateNodeId = (type: string, nodes: Node[]) => {
  const typePrefix = type.toLowerCase().replace(/[^a-z]/g, '_');
  const existingIds = nodes
    .filter(node => node.id.startsWith(`${typePrefix}_`))
    .map(node => parseInt(node.id.split('_')[1], 10))
    .filter(num => !isNaN(num));
  
  const nextNumber = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
  return `${typePrefix}_${nextNumber}`;
};

const FlowEditor: React.FC = () => {
  const navigate = useNavigate();
  const { workflowId } = useParams<{ workflowId: string }>();
  const { environment } = useEnvironment();
  const { enqueueSnackbar } = useSnackbar();
  const getAccessToken = useApiToken();
  
  // Use type assertions to bypass complex type issues
  const [nodes, setNodes, onNodesChange] = useNodesState<any>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<any>([]);
  
  const [workflowInputs, setWorkflowInputs] = useState<Record<string, any>>({});
  const [newInputKey, setNewInputKey] = useState('');
  const [newInputValue, setNewInputValue] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [showDiffModal, setShowDiffModal] = useState(false);
  const [originalWorkflow, setOriginalWorkflow] = useState<any>(null);
  const [showWorkflowIdModal, setShowWorkflowIdModal] = useState(false);
  const [showAddNodeModal, setShowAddNodeModal] = useState(false);
  const [showNodeEditModal, setShowNodeEditModal] = useState(false);
  const [showInputNodeModal, setShowInputNodeModal] = useState(false);
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);
  const [rightBottomDrawerOpen, setRightBottomDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [availableAgents, setAvailableAgents] = useState<AgentNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { fitView } = useReactFlow();
  const reactFlowInstance = useReactFlow();
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generationPrompt, setGenerationPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [notes, setNotes] = useState<any[]>([]);

  // Fetch workflow on mount
  useEffect(() => {
    if (workflowId && workflowId !== 'new') {
      fetchWorkflow();
    } else {
      setLoading(false);
    }
  }, [workflowId, environment]);

  const fetchWorkflow = async () => {
    if (!workflowId || workflowId === 'new') return;
    
    try {
      setLoading(true);
      const accessToken = await getAccessToken();
      const workflowService = new WorkflowService(environment, accessToken);
      const response = await workflowService.getWorkflow(workflowId);
      const workflow = response.data;
      
      if (workflow) {
        setOriginalWorkflow(workflow);
        initializeWorkflow(workflow);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch workflow', { variant: 'error' });
      console.error('Error fetching workflow:', error);
    } finally {
      setLoading(false);
    }
  };

  const initializeWorkflow = (workflow: any) => {
    const dependencies: Record<string, string[]> = {};
    workflow.nodes.forEach((node: any) => {
      if (node.depends_on && node.depends_on.length > 0) {
        dependencies[node._id] = node.depends_on;
      }
    });

    const flowNodes = workflow.nodes.map((node: any, index: number) => {
      const agent = AgentNode.fromJson(node);
      return {
        id: agent.id,
        type: 'base',
        position: {
          x: index * (NODE_WIDTH + GRID_PADDING),
          y: Math.floor(index / 3) * (NODE_HEIGHT + GRID_PADDING)
        },
        data: {
          label: agent.id,
          agent,
          type: node.type || 'agent',
          isDragging: false,
          onDelete: handleDeleteNode,
          onChangeLabel: handleLabelChange,
          onUpdate: handleNodeUpdate,
        },
        draggable: true
      };
    });

    if (workflow.additional_fields?.notes) {
      const stickyNotes = workflow.additional_fields.notes.map((note: any, index: number) => ({
        id: uuidv4(),
        type: 'stickyNote',
        position: { 
          x: Math.random() * 500, 
          y: Math.random() * 500 
        },
        data: {
          text: note.text,
          color: note.color,
          onChange: handleStickyNoteChange,
          onDelete: handleDeleteNode,
        },
        draggable: true,
      }));
      
      setNodes([...flowNodes, ...stickyNotes]);
      setNotes(workflow.additional_fields.notes);
    } else {
      setNodes(flowNodes);
    }

    setEdges(createEdgesFromNodes(flowNodes, dependencies));
    setWorkflowInputs(workflow.input || {});

    setTimeout(() => {
      fitView({ padding: 0.2 });
    }, 100);
  };

  const createEdgesFromNodes = useCallback((nodes: any[], existingDependencies: Record<string, string[]> = {}) => {
    const edges: any[] = [];
    nodes.forEach((node) => {
      const dependencies = existingDependencies[node.id] || [];
      dependencies.forEach((sourceId) => {
        edges.push({
          id: `e${sourceId}-${node.id}`,
          source: sourceId,
          target: node.id,
          animated: true,
          style: { stroke: 'rgb(206, 0, 179)' }
        });
      });
    });

    return edges;
  }, []);

  useEffect(() => {
    if (originalWorkflow) {
      setHasUnsavedChanges(true);
      setOriginalWorkflow(originalWorkflow);
      
      const dependencies: Record<string, string[]> = {};
      originalWorkflow.nodes.forEach(node => {
        if (node.depends_on && node.depends_on.length > 0) {
          dependencies[node._id] = node.depends_on;
        }
      });

      const flowNodes = originalWorkflow.nodes.map((node, index) => {
        const agent = AgentNode.fromJson(node);
        return {
          id: agent.id,
          type: 'base',
          position: {
            x: index * (NODE_WIDTH + GRID_PADDING),
            y: Math.floor(index / 3) * (NODE_HEIGHT + GRID_PADDING)
          },
          data: {
            label: agent.id,
            agent,
            type: node.type || 'agent',
            isDragging: false,
            onDelete: handleDeleteNode,
            onChangeLabel: handleLabelChange,
            onUpdate: handleNodeUpdate,
          },
          draggable: true
        };
      });

      if (originalWorkflow.additional_fields?.notes) {
        const stickyNotes = originalWorkflow.additional_fields.notes.map((note: any, index: number) => ({
          id: uuidv4(),
          type: 'stickyNote',
          position: { 
            x: Math.random() * 500, 
            y: Math.random() * 500 
          },
          data: {
            text: note.text,
            color: note.color,
            onChange: handleStickyNoteChange,
            onDelete: handleDeleteNode,
          },
          draggable: true,
        }));
        
        setNodes([...flowNodes, ...stickyNotes]);
        setNotes(originalWorkflow.additional_fields.notes);
      } else {
        setNodes(flowNodes);
      }

      setEdges(createEdgesFromNodes(flowNodes, dependencies));
      setWorkflowInputs(originalWorkflow.input || {});

      setTimeout(() => {
        fitView({ padding: 0.2 });
      }, 100);
    }
  }, [originalWorkflow, fitView, createEdgesFromNodes]);

  // Simple handlers that work with the type assertions
  const handleNodeDragStart = useCallback((event: React.MouseEvent, node: any) => {
    setNodes((nds: any) => 
      nds.map((n: any) => n.id === node.id ? { ...n, data: { ...n.data, isDragging: true } } : n)
    );
  }, []);

  const handleNodeDragStop = useCallback((event: React.MouseEvent, node: any) => {
    setNodes((nds: any) => 
      nds.map((n: any) => n.id === node.id ? { ...n, data: { ...n.data, isDragging: false } } : n)
    );
  }, []);

  const handleConnect = useCallback<OnConnect>((connection: Connection) => {
    if (connection.source === connection.target) {
      return;
    }

    const newEdge: any = {
      id: `e${connection.source}-${connection.target}`,
      source: connection.source!,
      target: connection.target!,
      animated: true,
      style: { stroke: 'rgb(206, 0, 179)' },
    };

    setEdges((prev: any) => addEdge(newEdge, prev));
    setHasUnsavedChanges(true);
  }, []);

  const handleNodesChange: OnNodesChange = useCallback((changes) => {
    onNodesChange(changes);
    setHasUnsavedChanges(true);
  }, [onNodesChange, setHasUnsavedChanges]);

  const handleEdgesChange: OnEdgesChange = useCallback((changes) => {
    onEdgesChange(changes);
    setHasUnsavedChanges(true);
  }, [onEdgesChange, setHasUnsavedChanges]);

  // Placeholder handlers - you can implement these as needed
  const handleDeleteNode = useCallback((id: string) => {
    setNodes((prevNodes: any) => prevNodes.filter((node: any) => node.id !== id));
    setEdges((prevEdges: any) => prevEdges.filter((edge: any) => edge.source !== id && edge.target !== id));
    setHasUnsavedChanges(true);
  }, []);

  const handleLabelChange = useCallback((id: string, newLabel: string) => {
    setNodes((prevNodes: any) =>
      prevNodes.map((node: any) =>
        node.id === id ? { ...node, data: { ...node.data, label: newLabel } } : node
      )
    );
    setHasUnsavedChanges(true);
  }, []);

  // 1. In FlowEditor, ensure handleNodeUpdate replaces the node in nodes
  const handleNodeUpdate = useCallback((updatedNode: any) => {
    setNodes((prevNodes: any) =>
      prevNodes.map((n: any) =>
        n.id === updatedNode.id ? { 
          ...updatedNode,
          data: {
            ...updatedNode.data,
            agent: updatedNode.data.agent ? (() => {
              // Create new ProceduralMemory instance
              const updatedProceduralMemory = updatedNode.data.agent.proceduralMemory ? 
                new ProceduralMemory(
                  updatedNode.data.agent.proceduralMemory.model,
                  updatedNode.data.agent.proceduralMemory.modelTemperature,
                  updatedNode.data.agent.proceduralMemory.steps,
                  updatedNode.data.agent.proceduralMemory.cognitiveAbilities,
                  updatedNode.data.agent.proceduralMemory.feedback
                ) : new ProceduralMemory('gpt-4o', 0.1, [], undefined, '');

              // Create new SemanticMemory instance
              const updatedSemanticMemory = updatedNode.data.agent.semanticMemory
                ? (updatedNode.data.agent.semanticMemory instanceof SemanticMemory 
                   ? updatedNode.data.agent.semanticMemory 
                   : SemanticMemory.fromJson(updatedNode.data.agent.semanticMemory))
                : new SemanticMemory(0.1, []);

              // Create new AgentNode instance with updated memories
              const existingAgent = n.data.agent;
              return new AgentNode(
                existingAgent.id,
                updatedSemanticMemory,
                updatedProceduralMemory,
                existingAgent.type || 'agent',
                existingAgent.childData,
                existingAgent.loopElementsKey,
                existingAgent.dependencies || [],
                existingAgent.parentAgentId,
                existingAgent.requiredParameters || [],
                existingAgent.agentDependencies || [],
                existingAgent.agent_template_id,
                existingAgent.uuid
              );
            })() : undefined
          }
        } : n
      )
    );
    setHasUnsavedChanges(true);
  }, []);

  const handleNodeClick = useCallback((node: any) => {
    setSelectedNode(node);
    if (node.data.type === 'input_node') {
      setShowInputNodeModal(true);
    } else {
      setShowNodeEditModal(true);
    }
  }, []);

  const handleAddInput = () => {
    if (!newInputKey.trim()) return;
    setWorkflowInputs(prev => ({
      ...prev,
      [newInputKey]: newInputValue
    }));
    setNewInputKey('');
    setNewInputValue('');
    setHasUnsavedChanges(true);
  };

  const handleDeleteInput = (key: string) => {
    setWorkflowInputs(prev => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
    setHasUnsavedChanges(true);
  };

  const handleInputChange = (key: string, value: string) => {
    setWorkflowInputs(prev => ({
      ...prev,
      [key]: value
    }));
    setHasUnsavedChanges(true);
  };

  const handleHtmlFormSubmit = (formData: Record<string, any>) => {
    setWorkflowInputs(prev => ({
      ...prev,
      ...formData
    }));
    setHasUnsavedChanges(true);
    enqueueSnackbar('Form data added to workflow inputs', { variant: 'success' });
  };

  const handleSaveWorkflow = () => {
    // For new workflows, always show the ID modal
    // For existing workflows, show the diff modal
    if (!originalWorkflow || workflowId === 'new') {
      setShowWorkflowIdModal(true);
    } else {
      setShowDiffModal(true);
    }
  };

  const handleSaveWithId = async (id: string, customWorkflowJson?: any) => {
    if (!id.trim()) {
      enqueueSnackbar('Please enter a workflow ID', { variant: 'error' });
      return;
    }

    try {
      setSaving(true);
      const accessToken = await getAccessToken();
      const workflowService = new WorkflowService(environment, accessToken);
      const updatedWorkflow = { ...(customWorkflowJson || workflowJson), id };

      // Check if we're editing an existing workflow or creating a new one
      if (originalWorkflow && originalWorkflow.id && workflowId !== 'new') {
        // Editing existing workflow - use update API
        await workflowService.updateWorkflow(originalWorkflow.id, updatedWorkflow);
        enqueueSnackbar('Workflow updated successfully', { variant: 'success' });
      } else {
        // Creating new workflow - use create API
        await workflowService.saveWorkflow(updatedWorkflow);
        enqueueSnackbar('Workflow created successfully', { variant: 'success' });
      }
      
      setShowWorkflowIdModal(false);
      setShowDiffModal(false);
      setHasUnsavedChanges(false);
      setOriginalWorkflow(updatedWorkflow);
      
      // Update the URL to reflect the saved workflow ID
      if (workflowId === 'new') {
        window.history.replaceState(null, '', `/flow-editor/${id}`);
      }
    } catch (error) {
      const action = originalWorkflow && originalWorkflow.id && workflowId !== 'new' ? 'update' : 'save';
      enqueueSnackbar(`Failed to ${action} workflow`, { variant: 'error' });
      console.error('Error saving workflow:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleExecuteWorkflow = () => {
    setShowExecuteModal(true);
  };

  const handleUploadWorkflow = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/json') {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target.result as string);
          
          const dependencies: Record<string, string[]> = {};
          jsonData.nodes.forEach(node => {
            if (node.depends_on && node.depends_on.length > 0) {
              dependencies[node._id] = node.depends_on;
            }
          });

          const flowNodes = jsonData.nodes.map((node, index) => {
            const agent = AgentNode.fromJson(node);
            return {
              id: agent.id,
              type: 'base',
              position: {
                x: index * (NODE_WIDTH + GRID_PADDING),
                y: Math.floor(index / 3) * (NODE_HEIGHT + GRID_PADDING)
              },
              data: {
                label: agent.id,
                agent,
                type: node.type,
                isDragging: false,
                onDelete: handleDeleteNode,
                onChangeLabel: handleLabelChange,
                onUpdate: handleNodeUpdate,
              },
              draggable: true
            };
          });

          setNodes(flowNodes);
          setEdges(createEdgesFromNodes(flowNodes, dependencies));
          setWorkflowInputs(jsonData.input || {});
          enqueueSnackbar('Workflow uploaded successfully', { variant: 'success' });

          setTimeout(() => {
            fitView({ padding: 0.2 });
          }, 100);
        } catch (error) {
          console.error('Error parsing workflow:', error);
          enqueueSnackbar('Error parsing workflow file', { variant: 'error' });
        }
      };
      reader.readAsText(file);
    } else {
      enqueueSnackbar('Please upload a valid JSON file', { variant: 'error' });
    }
    setIsModalOpen(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => enqueueSnackbar('Copied to clipboard', { variant: 'success' }))
      .catch(() => enqueueSnackbar('Failed to copy', { variant: 'error' }));
  };

  const handleCopyJson = () => {
    const jsonStr = JSON.stringify(originalWorkflow, null, 2);
    copyToClipboard(jsonStr);
  };

  const handleCopyJsonAsPython = () => {
    const pythonFormattedJson = JSON.stringify(originalWorkflow, null, 2)
      .replace(/null/g, 'None')
      .replace(/true/g, 'True')
      .replace(/false/g, 'False');
    const pythonDictStr = `workflow_dict = ${pythonFormattedJson}`;
    copyToClipboard(pythonDictStr);
  };

  const handleClearAll = useCallback(() => {
    // For now, we'll just clear all nodes without confirmation
    // TODO: Add confirmation dialog using MUI Dialog
    console.log('Clear All Nodes clicked');
    setNodes([]);
    setEdges([]);
    setHasUnsavedChanges(true);
  }, [setNodes, setEdges, setHasUnsavedChanges]);

  const handleGenerateWorkflow = async () => {
    if (!generationPrompt.trim()) {
      enqueueSnackbar('Please enter a generation prompt', { variant: 'error' });
      return;
    }

    try {
      setIsGenerating(true);
      // This is a placeholder - you can implement actual workflow generation logic here
      enqueueSnackbar('Workflow generation feature coming soon!', { variant: 'info' });
      setShowGenerateModal(false);
    } catch (error) {
      enqueueSnackbar('Failed to generate workflow', { variant: 'error' });
      console.error('Error generating workflow:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAddStickyNote = useCallback((color: string) => {
    const id = uuidv4();
    
    const newStickyNote = {
      id,
      type: 'stickyNote',
      position: { 
        x: Math.random() * 500, 
        y: Math.random() * 500 
      },
      data: {
        text: 'Double click to edit',
        color,
        onChange: handleStickyNoteChange,
        onDelete: handleDeleteNode,
      },
      draggable: true,
    };
    
    setNodes((prev: any) => [...prev, newStickyNote]);
    setNotes(prev => [...prev, { color, text: 'Double click to edit' }]);
    setHasUnsavedChanges(true);
  }, [setNodes, setHasUnsavedChanges]);

  const handleAddNode = useCallback(async (agent?: AgentNode, parameters?: Record<string, string>, dependencies?: AgentNode[]) => {
    if (!agent) {
      setShowAddNodeModal(true);
      return;
    }

    const nodeType = agent.type || 'agent';
    const nodeId = generateNodeId(nodeType, nodes);
    agent.id = nodeId;

    const position = {
      x: nodes.length * (NODE_WIDTH + GRID_PADDING),
      y: Math.floor(nodes.length / 3) * (NODE_HEIGHT + GRID_PADDING)
    };

    const newNode = {
      id: nodeId,
      type: 'base',
      position,
      data: {
        label: nodeId,
        agent,
        type: nodeType,
        isDragging: false,
        onDelete: handleDeleteNode,
        onChangeLabel: handleLabelChange,
        onUpdate: handleNodeUpdate,
      },
      draggable: true
    };

    setNodes((prev: any) => [...prev, newNode]);
    setHasUnsavedChanges(true);

    setTimeout(() => {
      fitView({ padding: 0.2 });
    }, 100);
  }, [nodes.length, fitView, setNodes, setHasUnsavedChanges]);

  const handleStickyNoteChange = useCallback((id: string, newText: string) => {
    setNodes((prevNodes: any) => 
      prevNodes.map((node: any) => {
        if (node.id === id && node.type === 'stickyNote') {
          return {
            ...node,
            data: { ...node.data, text: newText }
          };
        }
        return node;
      })
    );
    setHasUnsavedChanges(true);
  }, [setNodes, setHasUnsavedChanges]);

  const isSaveDisabled = useMemo(() => {
    const workflowNodes = nodes.filter((node: any) => node.type !== 'stickyNote');
    return workflowNodes.length === 0;
  }, [nodes]);

  const workflowJson = useMemo(() => {
    const cleanNodes = nodes.filter((node: any) => node.type !== 'stickyNote');
    const stickyNotes = nodes.filter((node: any) => node.type === 'stickyNote');

    const allVariables: Record<string, string> = {};
    cleanNodes.forEach((node: any) => {
      if (node.data?.agent?.proceduralMemory?.steps) {
        node.data.agent.proceduralMemory.steps.forEach((step: any) => {
          if (step.definition.variables) {
            Object.keys(step.definition.variables).forEach(key => {
              allVariables[key] = step.definition.variables[key] || '';
            });
          }
        });
      }
    });

    const json = {
      id: workflowId === 'new' ? uuidv4() : (workflowId || uuidv4()),
      is_enabled: true,
      input: {
        ...workflowInputs,
        ...allVariables
      },
      nodes: cleanNodes.map((node: any) => {
        const nodeId = node.data?.agent?.id || node.id;
        const dependsOn = edges
          .filter((edge: any) => edge.target === nodeId)
          .map((edge: any) => edge.source);

        const agent = node.data.agent;
        const nodeType = node.data.type || 'agent';

        const nodeData: any = {
          _id: nodeId,
          uuid: agent.uuid,
          type: nodeType,
          depends_on: dependsOn,
          semantic_memory_args: nodeType === 'workflow' ? null : agent.semanticMemory.toJson(),
          procedural_memory_args: agent.proceduralMemory.toJson(),
          working_memory_source: null,
          episodic_memory_source: null
        };

        if (nodeType === 'agent_template' && agent.id) {
          nodeData.agent_template_id = agent.agent_template_id || agent.id;
        }

        if (nodeType === 'workflow' && agent.workflowId) {
          nodeData.sub_workflow_id = agent.workflowId;
        }

        if (nodeType === 'loop' && agent.childData instanceof AgentNode) {
          const childJson = agent.childData.toJson();
          nodeData.child_data = {
            _id: childJson._id,
            uuid: childJson.uuid,
            type: 'agent',
            semantic_memory_args: null,
            procedural_memory_args: childJson.procedural_memory_args,
            working_memory_source: null,
            episodic_memory_source: null,
            depends_on: []
          };
          
          if (agent.loopElementsKey) {
            nodeData.loop_elements_key = agent.loopElementsKey;
          }
        }

        if (nodeType === 'input_node') {
          nodeData.required_parameters = agent.requiredParameters;
        }

        return nodeData;
      }),
      is_standard_output: true,
      response_template: null,
      additional_fields: {
        notes: stickyNotes.map((note: any) => ({
          color: note.data.color,
          text: note.data.text
        }))
      }
    };

    return json;
  }, [nodes, edges, workflowInputs, workflowId]);

  // 1. Replace Spin with CircularProgress in loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <CircularProgress size={48} />
      </div>
    );
  }

  return (
    <>
      <Box sx={{ 
        position: 'relative', 
        height: '100vh', 
        width: '100%',
        bgcolor: 'grey.50',
        overflow: 'hidden' // Remove scroll
      }}>
        <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onNodeDragStart={handleNodeDragStart}
            onNodeDragStop={handleNodeDragStop}
            onConnect={handleConnect}
            onNodeClick={(_, node) => handleNodeClick(node)}
            nodeTypes={nodeTypes as any}
            fitView
            proOptions={{ hideAttribution: true }}
            deleteKeyCode={['Backspace', 'Delete']}
            multiSelectionKeyCode={['Control', 'Meta']}
            selectionKeyCode={['Shift']}
            snapToGrid
            snapGrid={[20, 20]}
          >
            <Background color="#e5e7eb" gap={20} size={1} className="opacity-30" />
            <Controls 
              position="bottom-right"
              className="bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg shadow-lg"
              style={{ flexDirection: 'row', zIndex: 1202, margin: 16 }}
            />
            <FlowControls
              onClearAll={handleClearAll}
              onAddNode={() => handleAddNode()}
              onAddStickyNote={handleAddStickyNote}
              onSave={handleSaveWorkflow}
              onUpload={() => setIsModalOpen(true)}
              onShowInputs={() => setRightDrawerOpen(true)}
              onShowJson={() => setRightBottomDrawerOpen(true)}
              onExecute={handleExecuteWorkflow}
              isSaveDisabled={isSaveDisabled}
              nodeCount={nodes.length}
            />
          </ReactFlow>
      </Box>

      {/* Modals and Drawers */}
      {/* 2. Refactor Drawer usage to use MUI Drawer, with title/content inside */}
      <WorkflowInputsDrawer
        open={rightDrawerOpen}
        onClose={() => setRightDrawerOpen(false)}
        inputs={workflowInputs}
        newInputKey={newInputKey}
        newInputValue={newInputValue}
        onNewInputKeyChange={setNewInputKey}
        onNewInputValueChange={setNewInputValue}
        onAddInput={handleAddInput}
        onInputChange={handleInputChange}
        onDeleteInput={handleDeleteInput}
      />

      <Drawer anchor="right" open={rightBottomDrawerOpen} onClose={() => setRightBottomDrawerOpen(false)}>
        <Box sx={{ width: 500, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Workflow JSON</Typography>
          <Stack direction="column" spacing={2}>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<CopyIcon />} onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(workflowJson, null, 2));
                enqueueSnackbar('JSON copied to clipboard', { variant: 'success' });
              }}>
                Copy JSON
              </Button>
              <Button variant="outlined" startIcon={<CodeIcon />} onClick={() => {
                const pythonCode = `workflow = ${JSON.stringify(workflowJson, null, 2)}`;
                navigator.clipboard.writeText(pythonCode);
                enqueueSnackbar('Python code copied to clipboard', { variant: 'success' });
              }}>
                Copy as Python
              </Button>
            </Stack>
            <Accordion defaultExpanded>
              <AccordionSummary>
                <Typography variant="subtitle1">Workflow JSON</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <JSONView value={workflowJson} />
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Box>
      </Drawer>

      {isModalOpen && (
        <FileUploadModal onClose={() => setIsModalOpen(false)} onFileUpload={(workflow) => {
          // Handle file upload - you can implement this as needed
          enqueueSnackbar('Workflow uploaded successfully', { variant: 'success' });
          setIsModalOpen(false);
        }} />
      )}

      <ExecuteWorkflowModal
        open={showExecuteModal}
        onClose={() => setShowExecuteModal(false)}
        workflow={workflowJson}
        useJsonEndpoint={true}
        onFormSubmit={handleHtmlFormSubmit}
      />

      <UnsavedChangesModal
        open={showUnsavedModal}
        onSave={() => {
          handleSaveWorkflow();
          setShowUnsavedModal(false);
        }}
        onDiscard={() => {
          setShowUnsavedModal(false);
          if (pendingAction) {
            pendingAction();
            setPendingAction(null);
          }
        }}
        onCancel={() => {
          setShowUnsavedModal(false);
          setPendingAction(null);
        }}
      />

      <WorkflowIdModal
        open={showWorkflowIdModal}
        onClose={() => setShowWorkflowIdModal(false)}
        onSave={(id, workflowJson) => handleSaveWithId(id, workflowJson)}
        workflowId={workflowId === 'new' ? '' : workflowId}
        isEditing={!!originalWorkflow && workflowId !== 'new'}
        workflowJson={workflowJson}
      />

      <WorkflowDiffModal
        open={showDiffModal}
        onClose={() => setShowDiffModal(false)}
        onConfirm={handleSaveWithId}
        originalWorkflow={originalWorkflow}
        updatedWorkflow={workflowJson}
        isEditing={!!originalWorkflow && workflowId !== 'new'}
        loading={saving}
      />

      <AddNodeModal
        open={showAddNodeModal}
        onClose={() => setShowAddNodeModal(false)}
        onAdd={handleAddNode}
      />

      {selectedNode && (
        <>
          <NodeEditWizardModal
            open={showNodeEditModal}
            onClose={() => {
              setShowNodeEditModal(false);
              setSelectedNode(null);
            }}
            node={selectedNode}
            onUpdate={handleNodeUpdate}
            allNodes={nodes}
            getEdges={() => edges}
            setEdges={setEdges}
            workflowInputs={workflowInputs}
            setWorkflowInputs={setWorkflowInputs}
          />

          <InputNodeModal
            open={showInputNodeModal}
            onClose={() => {
              setShowInputNodeModal(false);
              setSelectedNode(null);
            }}
            node={selectedNode}
            onUpdate={handleNodeUpdate}
            workflowInputs={workflowInputs}
            setWorkflowInputs={setWorkflowInputs}
          />
        </>
      )}

      <GenerateWorkflowModal
        open={showGenerateModal}
        onClose={() => {
          setShowGenerateModal(false);
          setGenerationPrompt('');
        }}
        prompt={generationPrompt}
        onPromptChange={setGenerationPrompt}
        onGenerate={handleGenerateWorkflow}
        isGenerating={isGenerating}
      />
    </>
  );
};

const FlowEditorPage: React.FC = () => {
  return (
    <ReactFlowProvider>
      <FlowEditor />
    </ReactFlowProvider>
  );
};

export default FlowEditorPage;